import { LinkBreak } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useCorporateCostingProfileActions } from 'corporate/hooks';
import { ICorporateCostingProfileVendorQuote } from 'corporate/types';
import { IPricingProfile } from 'pricing/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  pricingProfile: IPricingProfile;
  vendorQuotes: ICorporateCostingProfileVendorQuote[];
  onDetachSuccess?: () => void;
  buttonElement?: ReactElement;
  buttonLabel?: string;
  notificationPosition?: NotificationPosition;
}

export const DetachVendorQuotesButton = ({
  pricingProfile,
  vendorQuotes,
  onDetachSuccess,
  buttonElement,
  buttonLabel,
  notificationPosition,
}: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { detachVendorQuotes } = useCorporateCostingProfileActions({
    selectionCount: vendorQuotes.length,
    position: notificationPosition,
  });

  const onDetach = useCallback(async () => {
    setIsConfirmationOpen(false);

    const vendorQuoteIds = vendorQuotes.map((vendorQuote) => vendorQuote.id);

    const { success } = await detachVendorQuotes({
      pricingProfileId: pricingProfile.id,
      ids: vendorQuoteIds,
    });

    if (!success) {
      return;
    }

    onDetachSuccess?.();
  }, [onDetachSuccess, vendorQuotes, detachVendorQuotes, pricingProfile.id]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={buttonLabel ?? t('buttonLabelDetach')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<LinkBreak />}
        onClick={() => setIsConfirmationOpen(true)}
      />
    );
  }, [buttonElement, buttonLabel, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleDetachVendorQuotes', {
          count: vendorQuotes.length,
        })}
        message={t('messageConfirmDetachVendorQuotes', {
          count: vendorQuotes.length,
        })}
        confirmBtnLabel={t('buttonLabelDetach')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onDetach}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
